/**
 * 템플릿 편집기 — 왼쪽 폼, 오른쪽 실시간 미리보기.
 *
 * 사이니지 편집의 핵심은 **입력하면서 결과를 보는 것**이다. 저장하고 화면에 나간
 * 뒤에 "글자가 잘렸다"를 알면 늦고, 그 사이 공공 화면에 잘린 문구가 노출된다.
 * 미리보기는 플레이어와 같은 렌더 코드를 쓰므로 여기서 본 것이 곧 화면이다.
 *
 * 저장 전 검증은 `@signageone/core` 의 스키마로 **클라이언트에서 먼저** 한다.
 * 서버가 최종 관문이지만, 왕복 없이 어느 칸이 잘못됐는지 즉시 알려주는 편이 낫다.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  BRAND_FONT_ID,
  brandFontStack,
  DEFAULT_TEMPLATE_FONT,
  TEMPLATE_FONT_IDS,
  TEMPLATE_FONTS,
  TEMPLATE_META,
  TEMPLATE_STARTERS,
  templateFontStack,
  templatePayloadSchema,
  type TemplateFontChoice,
  type TemplateId,
  type TemplatePayload,
} from '@signageone/core';
import { useBrand } from '../components/brand.js';
import { HistoryButton } from '../components/HistoryModal.js';
import type { Asset, SignageOneClient } from '../api/client.js';
import { ApiCallError } from '../api/client.js';
import { ErrorNote, Modal } from '../components/ui.js';
import { HelpTip } from '../components/HelpTip.js';
import { TemplatePreview } from '../components/TemplatePreview.js';
import { installTemplateFontFaces } from '../components/templateFonts.js';
import { FieldView, type FieldContext } from '../components/form/Fields.js';
import { focusFirstInvalid } from '../components/form/NumberInput.js';
import { stripIssuePrefix } from '../components/form/paths.js';
import { TEMPLATE_FORMS } from '../components/form/spec.js';
import type { MediaFieldHost } from '../components/form/MediaField.js';
import { parseTagList } from './AssetEditor.js';
import { useDirty, useGuardedClose } from '../components/closeGuard.js';
import { notify } from '../components/Toast.js';
import { useAsync } from '../hooks.js';
import { TemplateThumb } from '../components/MediaThumbs.js';
import { HtmlThumb } from '../components/HtmlContent.js';
import { layoutPatch, measureHtmlLayout } from '../components/htmlMeasure.js';

interface Props {
  client: SignageOneClient;
  /** 편집할 자산. 없으면 새로 만든다. */
  asset?: Asset | null;
  /** 새로 만들 때의 템플릿 종류. */
  templateId?: TemplateId;
  /**
   * 등록한 템플릿에서 새로 만든다(2026-10-30) — 그 콘텐츠의 내용·글꼴·태그를 그대로 채워 연다. 저장하면 새 콘텐츠이고 그
   * 콘텐츠는 그대로다.
   */
  from?: Asset | null;
  onClose: () => void;
  onSaved: () => void;
}

export function TemplateEditor({ client, asset, templateId, from, onClose, onSaved }: Props): ReactNode {
  const editing = Boolean(asset);
  const start = asset ?? from ?? null;
  const id = (start?.template?.id ?? templateId) as TemplateId;
  const meta = TEMPLATE_META[id];

  // 자산 메타(제목)와 템플릿 내용은 서로 다른 엔드포인트로 저장된다.
  // 노출 시간은 여기서 다루지 않는다 — 한 콘텐츠가 여러 편성에 들어가고 시간은 편성마다 다르다.
  const [title, setTitle] = useState(asset?.title ?? from?.template_entry?.name ?? from?.title ?? '');
  /* 태그도 여기서 붙인다 — 만든 뒤 따로 열어 달게 하면 아무도 달지 않는다. */
  const [tags, setTags] = useState((start?.tags ?? []).join(', '));
  const [data, setData] = useState<unknown>(() =>
    start?.template?.data ? structuredClone(start.template.data) : structuredClone(TEMPLATE_STARTERS[id]),
  );
  /* 글꼴은 내용이 아니라 모양이라 `data` 밖에 둔다 — 템플릿 종류가 달라도 같은 선택지다(번들 세 가지 + 사내 글꼴). */
  const [font, setFont] = useState<TemplateFontChoice>(start?.template?.font ?? DEFAULT_TEMPLATE_FONT);
  /* 고친 것이 있으면 닫기 전에 묻는다. */
  const close = useGuardedClose(useDirty({ title, tags, data, font }), onClose);
  /* 사내 글꼴 — 환경 설정에서 올린 파일이 있을 때만 고를 수 있다(이미 고른 템플릿은 그대로 보인다). */
  const { brand } = useBrand();
  const fontChoices: TemplateFontChoice[] =
    brand.font || font === BRAND_FONT_ID ? [...TEMPLATE_FONT_IDS, BRAND_FONT_ID] : [...TEMPLATE_FONT_IDS];

  // 고르기 견본이 그 글꼴로 보이게 콘솔 문서에도 글꼴을 싣는다(미리보기 iframe 은 따로 싣는다).
  useEffect(() => installTemplateFontFaces(), []);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<ReadonlyMap<string, string>>(new Map());

  /**
   * 편집 중인 값을 스키마로 파싱한다.
   *
   * 입력 도중에는 자주 무효하다(제목을 지웠다 다시 쓰는 중 등). 그때마다 미리보기를
   * 비우면 화면이 깜빡여 쓸 수 없다 — **마지막으로 유효했던 그림을 유지**하고
   * 무엇이 잘못됐는지만 알린다.
   */
  const parsed = useMemo(() => templatePayloadSchema.safeParse({ id, data, font }), [id, data, font]);

  // 마지막으로 유효했던 값을 ref 에 둔다. state 로 두면 렌더 중 setState 가 되고,
  // effect 로 미루면 미리보기가 한 프레임 늦어 입력이 끊겨 보인다. 순수한 캐시라
  // 렌더 중 갱신해도 관찰 가능한 차이가 없다.
  const lastValidRef = useRef<TemplatePayload | null>(null);
  if (parsed.success) lastValidRef.current = parsed.data;
  const lastValid = lastValidRef.current;
  // [움직임 다시 보기] — 미리보기가 나타날 때의 움직임을 다시 보인다(2026-11-03).
  const [replay, setReplay] = useState(0);

  const liveErrors = useMemo(() => {
    if (parsed.success) return new Map<string, string>();
    const map = new Map<string, string>();
    for (const issue of parsed.error.issues) {
      map.set(stripIssuePrefix(issue.path.join('.')), issue.message);
    }
    return map;
  }, [parsed]);

  // 서버가 준 오류가 있으면 그것을 우선한다 — 클라이언트가 놓친 규칙일 수 있다.
  const errors = fieldErrors.size > 0 ? fieldErrors : liveErrors;

  /*
   * 반반 화면의 그림·영상·HTML 칸 — 이 고객사의 이미지·영상·HTML 에서 고르거나 내 컴퓨터에서 올린다(태그는 이 템플릿에 적은
   * 것). 고른 것의 제목·미리보기는 칸이 묻는다.
   */
  const tagsRef = useRef(tags);
  tagsRef.current = tags;
  const media = useMemo<MediaFieldHost>(
    () => ({
      list: async () => {
        const [images, videos, html] = await Promise.all([
          client.allAssets({ type: 'image' }),
          client.allAssets({ type: 'video' }),
          client.allAssets({ type: 'html' }),
        ]);
        return [...images.items, ...videos.items, ...html.items].sort((a, b) => b.created_at.localeCompare(a.created_at));
      },
      upload: async (file) => {
        const parsed = parseTagList(tagsRef.current);
        const asset = await client.uploadAsset(file, 'error' in parsed ? [] : parsed.tags);
        // 올린 HTML 은 곧바로 잰다(콘텐츠 목록이 올린 뒤 하는 것과 같다) — 고정 크기로 만든 것이면 화면이 그 크기로 그려 칸에 맞춘다.
        if (asset.type !== 'html' || !asset.preview_url || asset.html_measured_at !== null) return asset;
        try {
          const layout = await measureHtmlLayout(asset.preview_url);
          return layout ? await client.updateAsset(asset.id, layoutPatch(layout)) : asset;
        } catch {
          return asset; // 재지 못하면 늘고 주는 HTML 로 띄운다 — 콘텐츠 목록이 다음에 다시 잰다
        }
      },
      describe: (assetId) => client.getAsset(assetId).catch(() => null),
    }),
    [client],
  );

  const ctx: FieldContext = {
    media,
    value: data,
    onChange: useCallback((next: unknown) => {
      setData(next);
      // 값을 고치면 서버 오류는 더 이상 유효하지 않다.
      setFieldErrors(new Map());
      setSaveError(null);
    }, []),
    errors,
    disabled: saving,
  };

  const save = async (): Promise<void> => {
    const result = templatePayloadSchema.safeParse({ id, data, font });
    if (!result.success) {
      const map = new Map<string, string>();
      for (const issue of result.error.issues) {
        map.set(stripIssuePrefix(issue.path.join('.')), issue.message);
      }
      setFieldErrors(map);
      setSaveError(`입력 오류 ${result.error.issues.length}곳을 확인하세요`);
      // 숫자 칸(빨간 칸)이 있으면 그리로 데려간다 — 긴 템플릿에서는 화면 밖이다.
      focusFirstInvalid(null);
      return;
    }
    if (!title.trim()) {
      setSaveError('제목을 입력하세요');
      return;
    }
    const parsedTags = parseTagList(tags);
    if ('error' in parsedTags) {
      setSaveError(parsedTags.error);
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      if (editing && asset) {
        // 내용을 먼저 저장한다 — 사용자가 바꾸려던 것이 이쪽이다.
        await client.updateTemplateContent(asset.id, result.data);
        // 제목·태그는 한 번에 보낸다. 바뀐 것만 담아 감사로그에 빈 수정이 쌓이지 않게.
        const patch: { title?: string; tags?: string[] } = {};
        if (title.trim() !== asset.title) patch.title = title.trim();
        if (parsedTags.tags.join('\n') !== asset.tags.join('\n')) patch.tags = parsedTags.tags;
        if (Object.keys(patch).length > 0) await client.updateAsset(asset.id, patch);
      } else {
        const created = (await client.createTemplateAsset({
          title: title.trim(),
          template: result.data,
          tags: parsedTags.tags,
        })) as Asset & { deduplicated?: boolean };
        // 같은 내용은 한 콘텐츠다 — 서버가 있던 것을 돌려준다. "만들었습니다" 라고 하면 없는 콘텐츠를 찾게 된다.
        if (created.deduplicated) {
          setSaveError(
            from && created.id === from.id
              ? '등록한 템플릿과 내용이 같습니다 — 바꿀 곳을 고친 뒤 저장하세요'
              : `같은 내용의 콘텐츠 「${created.title}」 가 이미 있습니다 — 그 콘텐츠를 쓰거나 내용을 고치세요`,
          );
          return;
        }
      }
      onSaved();
      notify(editing ? `"${title.trim()}" 을(를) 저장했습니다` : `"${title.trim()}" 을(를) 만들었습니다`);
    } catch (error) {
      if (error instanceof ApiCallError) {
        const issues = (error.details as { issues?: Array<{ path: string; message: string }> } | undefined)
          ?.issues;
        if (issues?.length) {
          setFieldErrors(new Map(issues.map((issue) => [stripIssuePrefix(issue.path), issue.message])));
          setSaveError(`서버 검증 실패: ${issues.length}곳`);
        } else {
          setSaveError(error.message);
        }
      } else {
        setSaveError(error instanceof Error ? error.message : '저장 실패');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={
        editing
          ? `${meta.name} 편집`
          : from
            ? `「${from.template_entry?.name ?? from.title}」 에서 새로 만들기`
            : `새 ${meta.name}`
      }
      onClose={close}
      wide
    >
      <div className="tpl-editor">
        {/* ── 왼쪽: 폼 ─────────────────────────────────────────── */}
        <div className="tpl-editor__form">
          {saveError ? <ErrorNote message={saveError} onDismiss={() => setSaveError(null)} /> : null}

          <section className="form-section">
            <h4 className="form-section__title">기본 정보</h4>
            <label className="field">
              <span className="field-label">
                <span>
                  제목
                  <HelpTip text="콘솔에서 콘텐츠를 구분하는 이름이며, 화면에는 표시되지 않습니다." label="제목 도움말" />
                </span>
              </span>
              <input
                className="input"
                value={title}
                maxLength={200}
                disabled={saving}
                placeholder="목록에서 이 콘텐츠를 부를 이름"
                onChange={(event) => setTitle(event.target.value)}
              />
            </label>
            <label className="field">
              <span className="field-label">
                <span>
                  태그
                  <HelpTip text="콘텐츠 목록에서 태그로 필터링할 수 있습니다." label="태그 도움말" />
                </span>
              </span>
              <input
                className="input"
                value={tags}
                disabled={saving}
                placeholder="로비, 가을 프로모션"
                onChange={(event) => setTags(event.target.value)}
              />
              <small className="hint">쉼표로 구분합니다.</small>
            </label>
            <p className="hint">{meta.useCase}</p>
          </section>

          {/*
            글꼴 — 번들 세 가지와, 환경 설정에서 올린 사내 글꼴. 이름만으로는 고를 수 없으니 그 글꼴로 쓴 견본을 보인다.
            고르면 오른쪽 미리보기가 바로 그 글꼴로 바뀐다(화면에서도 같은 파일로 그린다).
          */}
          <section className="form-section">
            <h4 className="form-section__title">글꼴</h4>
            <div className="font-picker" role="radiogroup" aria-label="글꼴">
              {fontChoices.map((fontId) => {
                const option =
                  fontId === BRAND_FONT_ID
                    ? {
                        name: '사내 글꼴',
                        description: brand.font
                          ? `환경 설정에서 올린 ${brand.font.filename}`
                          : '올린 사내 글꼴이 없어 기본 글꼴로 그립니다. 환경 설정에서 올리세요.',
                      }
                    : TEMPLATE_FONTS[fontId];
                const stack = fontId === BRAND_FONT_ID ? brandFontStack() : templateFontStack(fontId);
                return (
                  <label key={fontId} className={`font-option ${font === fontId ? 'is-on' : ''}`}>
                    <input
                      type="radio"
                      name="template-font"
                      value={fontId}
                      checked={font === fontId}
                      disabled={saving}
                      onChange={() => {
                        setFont(fontId);
                        setSaveError(null);
                      }}
                    />
                    <span className="font-option__sample" style={{ fontFamily: stack }}>
                      가나다 Aa 123
                    </span>
                    <span className="font-option__name">
                      {option.name}
                      {fontId === DEFAULT_TEMPLATE_FONT ? <span className="font-option__badge">기본</span> : null}
                    </span>
                    <span className="font-option__desc">{option.description}</span>
                  </label>
                );
              })}
            </div>
          </section>

          {TEMPLATE_FORMS[id].map((section) => (
            <section key={section.title} className="form-section">
              <h4 className="form-section__title">{section.title}</h4>
              {section.help ? <p className="hint">{section.help}</p> : null}
              <div className="field-stack">
                {section.fields.map((field) => (
                  <FieldView key={field.path} field={field} prefix="" ctx={ctx} />
                ))}
              </div>
            </section>
          ))}
        </div>

        {/* ── 오른쪽: 미리보기 ─────────────────────────────────── */}
        <div className="tpl-editor__preview">
          <div className="tpl-editor__preview-sticky">
            <div className="tpl-editor__preview-head">
              <span className="field-label">
                <span>
                  미리보기
                  <HelpTip
                    text="화면과 같은 방식으로 그립니다. 실제 화면의 헤더에는 화면 이름과 시각이 표시됩니다."
                    label="미리보기 도움말"
                  />
                </span>
              </span>
              {lastValid ? (
                <button type="button" className="btn btn-tiny ml-auto" onClick={() => setReplay((n) => n + 1)}>
                  움직임 다시 보기
                </button>
              ) : null}
              {parsed.success ? (
                <span className="preview-ok">입력 정상</span>
              ) : (
                <span className="preview-stale">마지막 정상 화면 · 오류 {liveErrors.size}곳</span>
              )}
            </div>

            {lastValid ? (
              <TemplatePreview template={lastValid} location="미리보기" live replay={replay} />
            ) : (
              <div className="tpl-preview tpl-preview--empty">
                <span>필수 항목을 채우면 미리보기가 나타납니다</span>
              </div>
            )}

            <div className="form-actions">
              {asset ? (
                <span className="mr-auto">
                  <HistoryButton client={client} type="template" id={asset.id} canRestore onRestored={onSaved} />
                </span>
              ) : null}
              <button type="button" className="btn" disabled={saving} onClick={close}>
                취소
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={saving}
                onClick={() => void save()}
              >
                {saving ? '저장 중…' : editing ? '저장' : '만들기'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

/**
 * 새 템플릿을 만들 때 무엇에서 시작할지 고른다 — 등록한 템플릿(2026-10-30, 콘텐츠 상세의 [템플릿으로 등록])과 기본 템플릿.
 * 등록한 것은 보관한 콘텐츠도 나온다(보관은 편성에서 빼는 것이지 틀을 버리는 것이 아니다).
 */
export function TemplatePicker({
  client,
  onPick,
  onPickRegistered,
  onClose,
}: {
  client: SignageOneClient;
  onPick: (id: TemplateId) => void;
  onPickRegistered: (asset: Asset) => void;
  onClose: () => void;
}): ReactNode {
  const registered = useAsync(() => client.allAssets({ archived: 'all', template_entry: 'true' }), [client]);
  const entries = (registered.data?.items ?? [])
    .filter((asset) => asset.template_entry && (asset.type === 'html' || (asset.type === 'template' && asset.template)))
    .sort((a, b) => a.template_entry!.name.localeCompare(b.template_entry!.name, 'ko'));
  return (
    <Modal title="템플릿 선택" onClose={onClose}>
      {entries.length > 0 ? (
        <section className="mb-4">
          <h4 className="form-section__title">등록한 템플릿</h4>
          <p className="hint">우리가 만든 화면입니다. 고르면 사본으로 새 콘텐츠를 만들고, 원래 콘텐츠는 그대로 남습니다.</p>
          <div className="tpl-picker">
            {entries.map((asset) => (
              <button
                key={asset.id}
                type="button"
                className="tpl-picker__item tpl-picker__item--entry"
                onClick={() => onPickRegistered(asset)}
              >
                <span className="tpl-picker__thumb" aria-hidden="true">
                  {asset.type === 'template' && asset.template ? (
                    <TemplateThumb template={asset.template} className="h-full w-full" location="" />
                  ) : (
                    <HtmlThumb asset={asset} className="h-full w-full" />
                  )}
                </span>
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span className="tpl-picker__name">{asset.template_entry!.name}</span>
                  {asset.template_entry!.description ? (
                    <span className="tpl-picker__use">{asset.template_entry!.description}</span>
                  ) : null}
                  <span className="tpl-picker__desc">{entryKind(asset)}</span>
                </span>
              </button>
            ))}
          </div>
        </section>
      ) : null}

      {entries.length > 0 ? <h4 className="form-section__title">기본 템플릿</h4> : null}
      <p className="hint">디자인은 고정되어 있으며 내용만 입력합니다.</p>

      <div className="tpl-picker">
        {Object.values(TEMPLATE_META).map((meta) => (
          <button key={meta.id} type="button" className="tpl-picker__item" onClick={() => onPick(meta.id)}>
            <span className="tpl-picker__name">{meta.name}</span>
            <span className="tpl-picker__use">{meta.useCase}</span>
            <span className="tpl-picker__desc">{meta.description}</span>
          </button>
        ))}
      </div>
      {registered.data && entries.length === 0 ? (
        <p className="hint mt-3">
          자주 쓰는 화면은 콘텐츠 상세의 [⋯] ▸ [템플릿으로 등록] 으로 여기에 두고 사본으로 새로 만들 수 있습니다.
        </p>
      ) : null}
    </Modal>
  );
}

/** 등록한 템플릿이 무엇으로 열리나 — "HTML · 1920×1080 · 화면 편집" · "공지 템플릿 · 내용 편집". */
function entryKind(asset: Asset): string {
  const archived = asset.archived ? ' · 보관된 콘텐츠' : '';
  if (asset.type === 'template' && asset.template) {
    return `${TEMPLATE_META[asset.template.id].name} 템플릿 · 내용 편집${archived}`;
  }
  const size = asset.width && asset.height ? ` · ${asset.width}×${asset.height}` : '';
  return `HTML${size} · 화면 편집${archived}`;
}
